import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const GRADE_SEARCH_FIELDS = ['grade', 'description'];
const GRADE_SORT_OPTIONS = [
  { key: 'grade', label: 'Grade', accessor: (g) => g.grade?.toLowerCase() ?? '' },
];


const GradeComponent = () => {
  const [grades, setGrades] = useState([]);
  const [currentGrade, setCurrentGrade] = useState({ grade: '', description: '' });
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [gradeToDelete, setGradeToDelete] = useState(null);
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  const {
    items: visibleGrades,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(grades, {
    searchFields: GRADE_SEARCH_FIELDS,
    sortOptions: GRADE_SORT_OPTIONS,
  });


  // Authentication check
  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
  }, [authState, navigate]);

  // Fetch all grades
  useEffect(() => {
    fetchGrades();
  }, []);

  const fetchGrades = async () => {
    const response = await fetch(`${backendURL}/api/grades`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    setGrades(data);
  };

  const handleFormChange = (e) => {
    setCurrentGrade({ ...currentGrade, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset modal states
    setShowGradeModal(false);
    setShowDescriptionModal(false);

    // Check if grade is empty
    if (!currentGrade.grade || currentGrade.grade.trim() === '') {
      setShowGradeModal(true);
      return;
    }

    // Check if description is empty
    if (!currentGrade.description || currentGrade.description.trim() === '') {
      setShowDescriptionModal(true);
      return;
    }

    if (currentGrade.grade_id) {
      await updateGrade(currentGrade.grade_id, currentGrade);
    } else {
      await createGrade(currentGrade);
    }
    setCurrentGrade({ grade: '', description: '' });
    setShowForm(false);
    fetchGrades();
  };


  const createGrade = async (gradeData) => {
    await fetch(`${backendURL}/api/grades`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gradeData),
      credentials: 'include'
    });
  };

  const updateGrade = async (gradeId, gradeData) => {
    await fetch(`${backendURL}/api/grades/${gradeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gradeData),
      credentials: 'include'
    });
  };

  const deleteGrade = async () => {
    if (gradeToDelete) {
      await fetch(`${backendURL}/api/grades/${gradeToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setGradeToDelete(null);
      setShowModal(false);
      fetchGrades();
    }
  };

  const editGrade = (grade) => {
    setCurrentGrade(grade);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentGrade({ grade: '', description: '' });
    setShowForm(false);
  }

  // Reset to a blank form when opening "Add New Grade" so a prior edit's data
  // doesn't linger after toggling back to the list and adding a new one.
  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentGrade({ grade: '', description: '' });
    }
    setShowForm(!showForm);
  };

  const gradeForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="grade">
          <Form.Label>Grade</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter grade"
            name="grade"
            onChange={handleFormChange}
            value={currentGrade.grade}
          />
        </Form.Group>
        <Form.Group controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            placeholder="Enter description"
            name="description"
            onChange={handleFormChange}
            value={currentGrade.description}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New Grade"
        listLabel="Show Grades List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Grades"
        form={gradeForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="grade"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput
              label="Search grades"
              placeholder="Grade or description"
              {...search}
            />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No grades have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleGrades.map((grade) => (
          <ExpandableRow
            key={grade.grade_id}
            main={{ title: grade.grade, subtitle: grade.description || 'No description provided.' }}
            actions={(
              <>
                <Button size="sm" onClick={() => editGrade(grade)} aria-label="Edit grade">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setGradeToDelete(grade.grade_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete grade"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteGrade}
        bodyText="Are you sure you want to delete this grade?"
      />
      <Modal show={showGradeModal} onHide={() => setShowGradeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Missing Grade</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please enter a grade.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGradeModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showDescriptionModal} onHide={() => setShowDescriptionModal(false)}>
      <Modal.Header closeButton>
          <Modal.Title>Missing Description</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please enter a description.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDescriptionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>


    </div>
  );
};

export default GradeComponent;
